import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, Check, X, FileText, DollarSign, Hammer, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return d.toLocaleDateString("pt-BR");
  } catch { return ""; }
}

const ICONES = {
  DOC_VENCIMENTO: FileText,
  PAGAMENTO: DollarSign,
  OBRA: Hammer,
  GERAL: Bell,
};

const CORES = {
  DOC_VENCIMENTO: "text-warning",
  PAGAMENTO: "text-success",
  OBRA: "text-info",
  GERAL: "text-muted-foreground",
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notificacoes");
      setItems(data.notificacoes || []);
      setNaoLidas(data.nao_lidas || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    // polling a cada 45s
    timerRef.current = setInterval(load, 45000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const marcarTodasLidas = async () => {
    await api.post("/notificacoes/marcar-lidas", {});
    load();
  };

  const excluir = async (id) => {
    await api.delete(`/notificacoes/${id}`);
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center border border-border rounded-sm hover:bg-accent transition-colors"
        aria-label="Notificações"
      >
        <Bell size={16} />
        {naoLidas > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-background"
          >
            {naoLidas > 9 ? "9+" : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-11 z-50 w-96 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-sm shadow-xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <div className="font-heading font-bold text-sm">Notificações</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
                {naoLidas} não lida{naoLidas === 1 ? "" : "s"}
              </div>
            </div>
            {naoLidas > 0 && (
              <Button
                data-testid="notif-marcar-todas"
                size="sm"
                variant="ghost"
                onClick={marcarTodasLidas}
                className="text-xs"
              >
                <Check size={12} className="mr-1" /> Marcar todas
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                Nada por aqui — 🟢 tudo em ordem.
              </div>
            ) : (
              items.map((n) => {
                const Icon = ICONES[n.tipo] || Bell;
                const cor = CORES[n.tipo] || "text-muted-foreground";
                return (
                  <div
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    className={`p-3 border-b border-border/50 flex items-start gap-3 group ${!n.lida ? "bg-accent/40" : ""}`}
                  >
                    <Icon size={16} className={`${cor} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold break-anywhere">{n.titulo}</div>
                      <div className="text-xs text-muted-foreground break-anywhere mt-0.5">{n.mensagem}</div>
                      <div className="text-[10px] text-muted-foreground font-mono mt-1">
                        {fmtRelative(n.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => excluir(n.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive flex-shrink-0"
                      data-testid={`notif-del-${n.id}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
